/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form } from 'react-bootstrap';
import {
  useForm,
  Controller,
  FieldValues,
  SubmitHandler
} from 'react-hook-form';
import { useQuery } from 'react-query';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import Flatpickr from 'react-flatpickr';
import FeatherIcon from 'feather-icons-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import { UzbekLatin } from 'flatpickr/dist/l10n/uz_latn.js';

import Button from 'components/base/Button';
import legalEntityService from 'service/legalEntity';
import warehouseService from 'service/warehouse';
import unitService from 'service/units';
import legalEntityCurrencyService from 'service/legalEntityCurrency';
import usersService from 'service/users';
import reportInvoiceService from 'service/reportInvoice';
import legalEntitySettingsService from 'service/legalEntitySettings';
import { pageToOffset } from 'utils/pageToOffset';

interface ProductCreateFilterProps {
  setGeneratedTableData?: any;
  savedValues?: any;
  setSavedValues?: any;
  setOpenModal: (show: boolean) => void;
  getName?: any;
  getBarcode?: any;
  setGetFilterData?: any;
  currentPage?: any;
  setPageCount?: any;
  setTotalAmount?: any;
}

const ProductCreateFilter = ({
  setGeneratedTableData,
  savedValues,
  setSavedValues,
  setOpenModal,
  getName,
  getBarcode,
  setGetFilterData,
  currentPage,
  setPageCount,
  setTotalAmount
}: ProductCreateFilterProps) => {
  const { t, i18n } = useTranslation();

  const statusOption = [
    {
      label: t('all'),
      value: 'all'
    },
    {
      label: t('returns'),
      value: 'returned'
    },
    {
      label: t('sales'),
      value: 'sale'
    },
    {
      label: t('canceled'),
      value: 'canceled'
    },
    {
      label: t('internet_store'),
      value: 'online'
    }
  ];
  const { control, handleSubmit, reset, watch, setValue } = useForm();
  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_pid
  );

  const branchId = useSelector((state: any) => state?.auth?.legal_entity?.pid);

  const [legalEntityOption, setLegalEntityOption] = useState<any>([]);
  const [warehouseOption, setWarehouseOption] = useState<any>([]);
  const [currencyOption, setCurrencyOption] = useState<any>([]);
  const [usersOption, setUsersOption] = useState<any>([]);
  const [unitsOption, setUnitsOption] = useState<any>([]);
  const [branchOption, setBranchOption] = useState<any>([]);
  const [dateFrom, setDateFrom] = useState<any>(null);
  const [dateTo, setDateTo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Initialize today's date in milliseconds
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const todayEnd = new Date().setHours(0, 0, 0, 0);

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  useEffect(() => {
    if (!dateFrom) {
      setDateFrom(todayStart);
    }
    if (!dateTo) {
      setDateTo(todayEnd);
    }
  }, [dateFrom, dateTo, setDateFrom, setDateTo]);

  // const handleStartDateChange = (date: Date | Date[]) => {
  //   if (Array.isArray(date)) {
  //     const startDate = new Date(date[0]);
  //     startDate.setHours(0, 0, 0, 0); // Set time to 00:00
  //     const startDateInMillis = startDate.getTime();
  //     setDateFrom(startDateInMillis);
  //   } else {
  //     const startDate = new Date(date);
  //     startDate.setHours(0, 0, 0, 0); // Set time to 00:00
  //     const startDateInMillis = startDate.getTime();
  //     setDateFrom(startDateInMillis);
  //   }
  // };

  // const handleEndDateChange = (date: Date | Date[]) => {
  //   if (Array.isArray(date)) {
  //     const endDateInMillis = date[0]?.getTime();
  //     setDateTo(endDateInMillis);
  //   } else {
  //     const endDateInMillis = date.getTime();
  //     setDateTo(endDateInMillis);
  //   }
  // };

  const handleStartDateChange = (date: Date | Date[] | null) => {
    if (!date) {
      console.error('No start date provided');
      return;
    }
    const startDate = Array.isArray(date) ? date[0] : date;
    if (startDate && !isNaN(startDate.getTime())) {
      setDateFrom(startDate.getTime());
      console.log(
        'Updated dateFrom:',
        new Date(startDate.getTime()).toLocaleString()
      );
    } else {
      console.error('Invalid start date:', date);
    }
  };

  const handleEndDateChange = (date: Date | Date[] | null) => {
    if (!date) {
      console.error('No end date provided');
      return;
    }
    const endDate = Array.isArray(date) ? date[0] : date;
    if (endDate && !isNaN(endDate.getTime())) {
      setDateTo(endDate.getTime());
      console.log(
        'Updated dateTo:',
        new Date(endDate.getTime()).toLocaleString()
      );
    } else {
      console.error('Invalid end date:', date);
    }
  };

  //Get by id
  const fetchData = () => {
    if (savedValues === '') return false;

    savedValues?.map((el: any) => {
      const computed = {
        provider: el?.supplier_id,
        payment_order: el?.payment_order_id,
        stock: el?.warehouse_id,
        currency: el?.currency_id,
        users: el?.employee_id,
        unit: el?.measure_unit_id,
        type: el?.type,
        grouped: el?.is_group ?? false,
        branch: el?.branch_id
      };
      reset(computed);
      setDateFrom(el?.date_from);
      setDateTo(el?.date_to);
    });
  };

  //GET LEgal Entity to options
  useQuery(['GET_BRANCH'], async () => {
    await legalEntityService
      .getListBranch({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.legal_entities?.map((option: any) => ({
          value: option?.pid || 0,
          label: option.name
        }));
        setBranchOption([{ value: 'all', label: t('all') }, ...options]);
      });
  });

  //GET LEgal Entity to options
  useQuery(['GET_LEGAL_ENTITY'], async () => {
    await legalEntityService
      .getLegalEntityOption({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.legal_entities?.map((option: any) => ({
          value: option?.pid || 0,
          label: option.name
        }));
        setLegalEntityOption(options);
      });
  });

  //GET Warehouse Entity to options
  useQuery(['GET_WAREHOUSE'], async () => {
    await warehouseService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.ware_houses?.map((option: any) => ({
          value: option?.pid || 0,
          label: option.name
        }));
        setWarehouseOption(options);
      });
  });

  //GET UNITS to options
  useQuery(['GET_UNITS'], async () => {
    await unitService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.measure_units?.map((option: any) => ({
          value: option?.pid || 0,
          label: option?.name?.[i18n?.language]
        }));
        setUnitsOption(options);
      });
  });

  //GET UNITS to options
  useQuery(['GET_USERS'], async () => {
    await usersService.getList({ offset: 0, limit: 100 }).then((res: any) => {
      const options = res?.data?.users?.map((option: any) => ({
        value: option?.id,
        label: option?.first_name
      }));
      setUsersOption(options);
    });
  });

  //GET Currency Entity to options
  useQuery(['GET_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.pid,
          label: option?.currency?.name?.[i18n?.language]
        }));
        setCurrencyOption(options);
      });
  });

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = data => {
    setLoading(true);
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    /* prettier-ignore */
    const adjustedDateTo =
      !dateFrom && !dateTo
        ? undefined
        : dateTo === dateFrom
          ? dateFrom + 86400000
          : dateTo
            ? dateTo + 86400000
            : undefined;

    const createFilterData = {
      // prettier-ignore
      date_from: dateFrom ? dateFrom : undefined,
      date_to: adjustedDateTo,
      supplier_pid: data?.provider ? data?.provider : undefined,
      currency_pid: data?.currency ? data?.currency : undefined,
      warehouse_pid: data?.stock ? data?.stock : undefined,
      employee_id: data?.users ? data?.users : undefined,
      measure_unit_pid: data?.unit ? data?.unit : undefined,
      type: data?.type ? data?.type : undefined,
      is_group: data?.grouped ? data?.grouped : undefined,
      branch_pid: data?.branch ? data?.branch : undefined
    };
    setGetFilterData(createFilterData);

    reportInvoiceService
      .getReportSaleList({
        limit: 10,
        offset: pageToOffset(currentPage, 10),
        name: getName,
        barcode: getBarcode,
        ...createFilterData
      })
      .then((res: any) => {
        if (res) {
          setPageCount(Math.ceil(res?.count / 10));
          setTotalAmount(res?.total_amount);

          const resData =
            res?.list?.map((items: any) => {
              const packageQuantity = items?.product?.package_quantity || 1;
              const quantity = items?.quantity
                ? Number(items?.quantity.toFixed(3))
                : 0;
              const fullPackages = Math.floor(quantity / packageQuantity);
              const remainingQuantity = quantity % packageQuantity;
              const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

              return {
                product_name: items?.product?.name,
                barcode: items?.product?.barcode,
                warehouse: items?.product?.ware_house?.name,
                unit: items?.product?.nomenclature?.measure_unit?.name?.[
                  i18n?.language
                ],
                currency: items?.currency?.name?.[i18n?.language],
                buyyer: items?.user?.first_name,
                category:
                  items?.product?.nomenclature?.category?.name?.[
                    i18n?.language
                  ],
                purchase_price: items?.price_in
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.price_in)
                      .replace(/,/g, ' ')
                  : 0,
                paid: items?.payment_amount
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.payment_amount)
                      .replace(/,/g, ' ')
                  : 0,
                discrepancy: items?.payment_difference
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.payment_difference)
                      .replace(/,/g, ' ')
                  : 0,
                sales_price: items?.price
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.price)
                      .replace(/,/g, ' ')
                  : 0,
                total_payment_price: items?.total_payment_amount
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.total_payment_amount)
                      .replace(/,/g, ' ')
                  : 0,
                payment_procedure: items?.payment_order?.name?.[i18n?.language],
                supplier: items?.legal_entity?.name,
                quantity:
                  items?.product?.nomenclature?.measure_unit_kind_pid === 1
                    ? quantityDisplay
                    : quantity,
                /* prettier-ignore */
                date: formatDateToTimeZone(items?.date_create, timeZone),
                /* prettier-ignore */
                date_synchronization: formatDateToTimeZone(items?.date_sync, timeZone),
                serial_number: items?.partion_codes
              };
            }) ?? [];

          if (resData) {
            setGeneratedTableData(resData);
            setSavedValues([
              {
                date_from: dateFrom ? dateFrom : undefined,
                date_to: dateTo ? dateTo : undefined,
                supplier_id: data?.provider ? data?.provider : undefined,
                measure_unit_id: data?.unit ? data?.unit : undefined,
                currency_id: data?.currency ? data?.currency : undefined,
                warehouse_id: data?.stock ? data?.stock : undefined,
                employee_id: data?.users ? data?.users : undefined,
                type: data?.type ? data?.type : undefined,
                is_group: data?.grouped ? data?.grouped : undefined,
                branch_id: data?.branch ? data?.branch : undefined
              }
            ]);
            setOpenModal(false);
            setLoading(false);
          }
        }
      });
  };

  const handleClear = () => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    reportInvoiceService
      .getReportSaleList({
        limit: 10,
        offset: pageToOffset(currentPage, 10),
        currency_id: currencyId,
        branch_id: branchId,
        date_from: todayStart,
        date_to: todayEnd + 86400000,
        name: getName,
        barcode: getBarcode
      })
      .then((res: any) => {
        if (res) {
          setPageCount(Math.ceil(res?.count / 10));
          setTotalAmount(res?.total_amount);

          const resData =
            res?.list?.map((items: any) => {
              const packageQuantity = items?.product?.package_quantity || 1;
              const quantity = items?.quantity
                ? Number(items?.quantity.toFixed(3))
                : 0;
              const fullPackages = Math.floor(quantity / packageQuantity);
              const remainingQuantity = quantity % packageQuantity;
              const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

              return {
                product_name: items?.product?.name,
                barcode: items?.product?.barcode,
                warehouse: items?.product?.ware_house?.name,
                unit: items?.product?.nomenclature?.measure_unit?.name?.[
                  i18n?.language
                ],
                currency: items?.currency?.name?.[i18n?.language],
                buyyer: items?.user?.first_name,
                category:
                  items?.product?.nomenclature?.category?.name?.[
                    i18n?.language
                  ],
                purchase_price: items?.price_in
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.price_in)
                      .replace(/,/g, ' ')
                  : 0,
                paid: items?.payment_amount
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.payment_amount)
                      .replace(/,/g, ' ')
                  : 0,
                discrepancy: items?.payment_difference
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.payment_difference)
                      .replace(/,/g, ' ')
                  : 0,
                sales_price: items?.price
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.price)
                      .replace(/,/g, ' ')
                  : 0,
                total_payment_price: items?.total_payment_amount
                  ? new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 4,
                      useGrouping: true
                    })
                      .format(items?.total_payment_amount)
                      .replace(/,/g, ' ')
                  : 0,
                payment_procedure: items?.payment_order?.name?.[i18n?.language],
                supplier: items?.legal_entity?.name,
                quantity:
                  items?.product?.nomenclature?.measure_unit_kind_pid === 1
                    ? quantityDisplay
                    : quantity,
                /* prettier-ignore */
                date: formatDateToTimeZone(items?.date_create, timeZone),
                /* prettier-ignore */
                date_synchronization: formatDateToTimeZone(items?.date_sync, timeZone),
                serial_number: items?.partion_codes
              };
            }) ?? [];

          if (resData) {
            setGeneratedTableData(resData);
            reset({
              provider: '',
              branch: '',
              stock: '',
              currency: '',
              unit: '',
              users: '',
              type: '',
              grouped: false
            });
            setDateFrom(null);
            setDateTo(null);
            setSavedValues([]);
            setOpenModal(false);
          }
        }
      });
  };

  const getFlatpickrLocale = () => {
    switch (i18n.language) {
      case 'ru':
        return Russian;
      case 'uz':
        return UzbekLatin;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (savedValues.length > 0) {
      fetchData();
    }
  }, [savedValues]);

  useEffect(() => {
    if (currencyId) {
      const defaultCurrency = currencyOption.find(
        (option: any) => option.value === currencyId
      );
      if (defaultCurrency && savedValues?.length === 0) {
        setValue('currency', defaultCurrency.value);
      }
    }
  }, [currencyId, currencyOption, savedValues]);

  useEffect(() => {
    if (branchId) {
      const defaultBranch = branchOption.find(
        (option: any) => option.value === branchId
      );
      if (defaultBranch && savedValues?.length === 0) {
        setValue('branch', defaultBranch.value);
      }
    }
  }, [branchId, branchOption, savedValues]);

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: '10px'
        }}
      >
        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="branch"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {branchOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('branch')}</label>
                  {watch('branch') && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('branch', null);
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="provider"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {legalEntityOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('supplier')}</label>
                  {watch('provider') && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('provider', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="stock"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {warehouseOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('warehouse')}</label>
                  {watch('stock') && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('stock', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="unit"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {unitsOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">
                    {t('measure_unit')}
                  </label>
                  {watch('unit') && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('unit', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="users"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {usersOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('employees')}</label>
                  {watch('users') !== '' && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('users', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="currency"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {currencyOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('currency')}</label>
                  {watch('currency') && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('currency', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Floating>
          <Flatpickr
            className={classNames('form-control')}
            style={{ height: '48px' }}
            options={{
              nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
              prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
              locale: getFlatpickrLocale(),
              monthSelectorType: 'static',
              onDayCreate: (...[, , , dayElem]) => {
                if (
                  dayElem.dateObj.getDay() === 5 ||
                  dayElem.dateObj.getDay() === 6
                ) {
                  dayElem.className += ' weekend-days';
                }
              },
              dateFormat: 'd-m-Y H:i',
              enableTime: true,
              static: true,
              disableMobile: true,
              defaultHour: new Date().getHours(),
              defaultMinute: new Date().getMinutes(),
              parseDate: (dateStr: string) => {
                const timeOnlyRegex = /^\d{1,2}:\d{2}$/;
                if (timeOnlyRegex.test(dateStr)) {
                  const today = new Date();
                  const [hours, minutes] = dateStr.split(':').map(Number);
                  return new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate(),
                    hours,
                    minutes
                  );
                }
                try {
                  const parsed = new Date(dateStr);
                  return isNaN(parsed.getTime()) ? new Date() : parsed;
                } catch (e) {
                  console.error('Failed to parse date:', dateStr);
                  return new Date();
                }
              }
            }}
            value={dateFrom ? new Date(dateFrom) : undefined}
            onChange={handleStartDateChange}
            placeholder={t('date_from')}
          />
        </Form.Floating>

        <Form.Floating>
          <Flatpickr
            className={classNames('form-control')}
            style={{ height: '48px' }}
            options={{
              nextArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"/></svg>`,
              prevArrow: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M224 480c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25l192-192c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25L77.25 256l169.4 169.4c12.5 12.5 12.5 32.75 0 45.25C240.4 476.9 232.2 480 224 480z"/></svg>`,
              locale: getFlatpickrLocale(),
              monthSelectorType: 'static',
              onDayCreate: (...[, , , dayElem]) => {
                if (
                  dayElem.dateObj.getDay() === 5 ||
                  dayElem.dateObj.getDay() === 6
                ) {
                  dayElem.className += ' weekend-days';
                }
              },
              dateFormat: 'd-m-Y H:i',
              enableTime: true,
              static: true,
              disableMobile: true,
              defaultHour: new Date().getHours(),
              defaultMinute: new Date().getMinutes(),
              parseDate: (dateStr: string) => {
                const timeOnlyRegex = /^\d{1,2}:\d{2}$/;
                if (timeOnlyRegex.test(dateStr)) {
                  const today = new Date();
                  const [hours, minutes] = dateStr.split(':').map(Number);
                  return new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate(),
                    hours,
                    minutes
                  );
                }
                try {
                  const parsed = new Date(dateStr);
                  return isNaN(parsed.getTime()) ? new Date() : parsed;
                } catch (e) {
                  console.error('Failed to parse date:', dateStr);
                  return new Date();
                }
              }
            }}
            value={dateTo ? new Date(dateTo) : undefined}
            onChange={handleEndDateChange}
            placeholder={t('date_to')}
          />
        </Form.Floating>

        <Form.Group>
          <div className="react-select-container position-relative">
            <Controller
              name="type"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {statusOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('type')}</label>
                  {watch('type') !== '' && (
                    <Button
                      variant="link"
                      style={{
                        position: 'absolute',
                        right: '5%',
                        top: '15%',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setValue('type', '');
                      }}
                    >
                      <FeatherIcon icon="delete" size={16} />
                    </Button>
                  )}
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group className="d-flex gap-3 mt-2">
          <Controller
            name="grouped"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Form.Check
                {...field}
                label={t('group')}
                checked={field.value}
                onChange={e => field.onChange(e.target.checked)}
              />
            )}
          />
        </Form.Group>

        <Form.Group style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
          <Button
            style={{ height: '48px', width: '100%' }}
            variant="outline-primary"
            type="button"
            onClick={() => handleClear()}
          >
            {t('reset')}
          </Button>
          <Button
            style={{ height: '48px', width: '100%' }}
            variant="primary"
            type="submit"
            loading={loading}
            loadingPosition="start"
          >
            {t('apply')}
          </Button>
        </Form.Group>
      </Form>
    </>
  );
};

export default ProductCreateFilter;
